import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { useProducts } from '../../bff-api/products/useProducts';
import { EnhancedError } from '../../components/_error/EnhancedError';
import { Layout } from '../../components/layout/Layout';
import { Blocks } from '../../components/sfcc-content-rendering/Blocks';
import { RecaptchaAction } from '../../recaptcha/recaptcha';
import { breakpoints } from '../../theme/foundations/breakpoints';
import { Breadcrumb, Header } from '../../ui-components';
import { useBreadcrumbs } from '../../ui-components/breadcrumbs/useBreadcrumbs';
import { GiftCardsSchema } from '../../utils/schema/GiftCardsSchema';
import { useContentData } from '../show/hooks/useContentData';
import styles from './GiftCards.module.scss';
import {
    GiftCardsBreadcrumbsSkeleton,
    GiftCardsCardContainerSkeleton,
    GiftCardsFAQCardSkeleton,
    GiftCardsTitleSkeleton,
    GiftCardsYouGiveCardSkeleton,
} from './components/GiftCardsSkeleton';
import { GiftCardsYouGiveSection } from './components/GiftCardsYouGiveSection';
import { CheckBalanceModal } from './components/check-balance-modal';
import { convertFAQItemsToSchemaItems, getFAQItems } from './utils/faq';
import { getRootCategory } from './utils/getRootCategory';
import type { CategoryInfo } from '../show/api/getContentData';

const pgcSku = 'GCP-15269';
const egcSku = 'GCP-15271';

export const GiftCardsLayout = (): ReactElement | null => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const { data, isFetching: isLoading } = useProducts([pgcSku, egcSku]);
    const { physicalCardProduct } = useMemo(
        () => ({
            physicalCardProduct: data?.find((product) => product.id === pgcSku),
        }),
        [data],
    );

    const { data: contentData } = useContentData({ categoryPath: '/gifts/gift-cards' });
    const categoryInfo = contentData as CategoryInfo;

    const intl = useIntl();
    const breadcrumbs = useBreadcrumbs(
        getRootCategory(physicalCardProduct?.c_primaryCategory?.parentCategoryTree),
    );

    const faqItems = getFAQItems(intl, onOpen);
    const faqSchemaItems = convertFAQItemsToSchemaItems(faqItems);

    return (
        <Layout>
            <GiftCardsSchema breadcrumbTrail={breadcrumbs} faqItems={faqSchemaItems} />
            <Flex
                direction="column"
                gap={{ base: 8 }}
                paddingBottom={{ base: 8, lg: 16 }}
                paddingX={{ md: 8, lg: 10, xl: 16 }}
                data-testid="gift-cards-layout"
                maxWidth={breakpoints.xl}
                marginX="auto"
            >
                <CheckBalanceModal
                    id="check-balance-modal"
                    isOpen={isOpen}
                    onClose={onClose}
                    action={RecaptchaAction.GIFT_CARD_BALANCE_LANDING_PAGE}
                />
                <Flex direction="column">
                    <GiftCardsBreadcrumbsSkeleton isLoaded={!isLoading}>
                        <Breadcrumb itemsList={breadcrumbs} />
                    </GiftCardsBreadcrumbsSkeleton>
                    <GiftCardsTitleSkeleton isLoaded={!isLoading}>
                        <Header as="h2" variant="header2" style="bold" color="slt-pepper.1000">
                            <FormattedMessage id="gift_cards.title" defaultMessage="Gift Cards" />
                        </Header>
                    </GiftCardsTitleSkeleton>
                </Flex>

                <Flex gap={{ base: 8, md: 16 }} direction="column">
                    {isLoading ? (
                        <>
                            <GiftCardsCardContainerSkeleton />
                            <GiftCardsCardContainerSkeleton />
                        </>
                    ) : (
                        <Blocks
                            id="cat-landing-slot-2"
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                            className={styles.giftCardsContent}
                        />
                    )}

                    <GiftCardsYouGiveCardSkeleton isLoaded={!isLoading}>
                        <GiftCardsYouGiveSection onCheckBalanceClick={onOpen} />
                    </GiftCardsYouGiveCardSkeleton>

                    <GiftCardsFAQCardSkeleton isLoaded={!isLoading}>
                        <Blocks
                            id="seo-content-block-js-css"
                            contentBlocks={categoryInfo?.response?.categoryContent?.contentAssets}
                            className="hidden"
                        />

                        <Blocks
                            id="cat-landing-bottom-block"
                            contentBlocks={categoryInfo?.response?.categoryContent?.categorySlots}
                            className={styles.faqContent}
                        />
                    </GiftCardsFAQCardSkeleton>
                </Flex>
            </Flex>
        </Layout>
    );
};

const GiftCards: PageFC = () => (
    <ErrorBoundary FallbackComponent={EnhancedError}>
        <GiftCardsLayout />
    </ErrorBoundary>
);

export { GiftCards };
